import * as Sentry from '@sentry/browser';
import type { Breadcrumb, Event } from '@sentry/browser';
import { UserModel } from '../modules/auth';

// Sentry has no BAA: nothing sent to it may carry PHI/PII (emails, names, request payloads).
// Emails are masked raw or URL-encoded (%40); URL query strings and fragments are dropped.
const EMAIL_RE = /[^\s@<>"'`(),;:/\\[\]{}?=&]+(?:@|%40)[^\s@<>"'`(),;:/\\[\]{}%]+\.[a-z]{2,}/gi;
const QUERY_RE = /(\/[^\s"'`\\?#]*)[?#][^\s"'`\\]*/g;
const BARE_QUERY_RE = /"\?[^"\\]*"/g; // a JSON string that is only a query, e.g. span data http.query
const maskEmails = (s: string) => s.replace(EMAIL_RE, '[email]');
const scrubText = (s: string) => maskEmails(s.replace(QUERY_RE, '$1').replace(BARE_QUERY_RE, '""'));
const scrubJson = <T>(value: T): T => JSON.parse(scrubText(JSON.stringify(value)));

// Event extras: call-site labels, free-text messages and primitive ids (numeric or uuid).
// Everything else (request payloads, client/provider objects) is dropped.
const TEXT_KEYS = ['module', 'func', 'msg', 'message'];
const ID_KEY_RE = /^(u?uids?|ids?)$|_(u?uids?|ids?)$|[a-z](Uuids?|Ids?)$/;
const ID_VALUE_RE = /^(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{32})$/i;
const isId = (v: any) => typeof v === 'number' || (typeof v === 'string' && ID_VALUE_RE.test(v));
const isIdValue = (v: any) => isId(v) || (Array.isArray(v) && v.every(isId));

const scrubExtra = (extra: Record<string, any>) => {
  const out: Record<string, any> = {};
  const dropped: string[] = [];
  Object.entries(extra).forEach(([key, value]) => {
    if (TEXT_KEYS.includes(key) && typeof value === 'string') out[key] = value;
    else if (ID_KEY_RE.test(key) && isIdValue(value)) out[key] = value;
    else dropped.push(key);
  });
  if (dropped.length) out.dropped = dropped;
  return out;
};

const sentryBeforeBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb | null => {
  // Console messages are the raw console arguments (names, payloads): never send them.
  if (breadcrumb.category === 'console') return null;
  const { category, data } = breadcrumb;
  const crumb =
    (category === 'xhr' || category === 'fetch') && data
      ? { ...breadcrumb, data: { method: data.method, url: data.url, status_code: data.status_code } }
      : breadcrumb;
  try {
    return scrubJson(crumb);
  } catch {
    return null;
  }
};

// Errors, transactions (their span URLs) and replay events. Breadcrumbs were scrubbed when added.
const sentryBeforeSend = <T extends Event>(event: T): T | null => {
  const e: any = { ...event };
  if (e.extra) e.extra = scrubExtra(e.extra);
  if (e.request) {
    e.request = { ...e.request };
    delete e.request.query_string;
    delete e.request.data;
    delete e.request.cookies;
  }
  try {
    return scrubJson(e);
  } catch {
    if (e.type) return null; // transaction or replay event
    // Unscrubbable error: still report it, with only fields that cannot carry PHI.
    const { event_id, timestamp, level, platform, release, environment } = e;
    return {
      event_id,
      timestamp,
      level,
      platform,
      release,
      environment,
      message: 'Event replaced: PHI scrub failed',
      exception: { values: e.exception?.values?.map((v: any) => ({ type: v.type, stacktrace: v.stacktrace })) },
    } as T;
  }
};

// Replay masks text and inputs, not attributes: labels carry client emails, phones, names.
const REPLAY_MASK_ATTRIBUTES = ['title', 'placeholder', 'aria-label', 'alt'];

// URL attributes (href, src, srcset, style url(), object data) bypass maskAttributes in rrweb, and
// beforeAddRecordingEvent never sees DOM snapshots/mutations. So elements whose URL can carry PHI
// (contact links, query strings, emails, signed S3 urls) are blocked: recorded as an empty
// same-size placeholder, and rrweb skips attribute mutations on them.
// '#' is left out: hundreds of in-app anchors use href="#". Head <link>s (font css) are static.
const REPLAY_BLOCK_SELECTORS = [
  'a[href^="mailto:" i]',
  'a[href^="tel:" i]',
  'a[href^="sms:" i]',
  '[href*="?"]:not(link)',
  '[href*="@"]',
  '[src*="?"]',
  '[srcset*="?"]',
  '[style*="url("]',
  'iframe',
  'object',
  'embed',
];

// Sentry SDK 7.107 calls beforeAddRecordingEvent for custom events (type 5: navigation and
// network spans) only, never for DOM snapshots, mutations or meta. Those are covered by block above.
const sentryBeforeAddRecordingEvent = (event: any) => (event.type === 5 ? scrubJson(event) : event);

const REPLAY_OPTIONS = {
  maskAllText: true,
  blockAllMedia: true,
  maskAttributes: REPLAY_MASK_ATTRIBUTES,
  block: REPLAY_BLOCK_SELECTORS,
  beforeAddRecordingEvent: sentryBeforeAddRecordingEvent,
};

// The feedback widget (feedbackIntegration with autoInject: false) is shown to signed-in providers only:
// clients, guardians and public pages never get it.
const monitoringSyncFeedbackWidget = (user?: UserModel) => {
  const feedback = Sentry.getClient()?.getIntegrationByName?.<ReturnType<typeof Sentry.feedbackIntegration>>('Feedback');
  if (!feedback) return;
  if (!user?.provider) feedback.remove();
  else if (!feedback.getWidget()) feedback.createWidget();
};

const monitoringSetUser = (user: UserModel) => {
  if (user) {
    const initials = user.initials || user.provider?.initials || user.client?.initials || user.guardian?.initials;
    Sentry.setUser({ id: user.uuid, name: initials });
  }
};

const monitoringCaptureError = (err: any, extra?: any) => {
  console.error('Error: ', err, extra);
  Sentry.captureException(err, {
    extra: extra || {},
  });
};

const monitoringCaptureMessage = (message: string) => {
  Sentry.captureMessage(message);
};

export {
  REPLAY_BLOCK_SELECTORS,
  REPLAY_MASK_ATTRIBUTES,
  REPLAY_OPTIONS,
  monitoringSetUser,
  monitoringCaptureError,
  monitoringCaptureMessage,
  monitoringSyncFeedbackWidget,
  sentryBeforeSend,
  sentryBeforeBreadcrumb,
  sentryBeforeAddRecordingEvent,
};
